// src/SystemSettingsPage.js — System settings and configuration
//
// These used to be five toggles in React state, next to a comment reading "in a
// real app, save to a settings table", and a save button that waited half a
// second and said "Settings saved successfully!". Nothing was stored and
// nothing read them.
//
// They come from platform_settings now, which is also where the commission rate
// lives that capture-payment and create-payment-intent read at the moment they
// take money. Changing a value here changes what people are charged, so every
// change is written to the admin audit log with the old value and the new.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Switch, FormControlLabel, Divider,
  Alert, Grid, Card, CardContent, CircularProgress,
} from '@mui/material';
import { supabase } from './supabaseClient';

import { prettyStatus } from './labels';
// Grouped so money sits apart from housekeeping. Anything not listed still
// appears, under "Other", rather than being hidden because this map is stale.
const GROUPS = [
  ['Money', ['commission_percent', 'earnings_hold_days']],
  ['Booking rules', ['cancellation_window_hours', 'min_booking_notice_hours',
                     'max_travel_miles', 'default_service_radius_miles']],
  ['Access', ['maintenance_mode', 'allow_new_signups', 'require_phone_verification', 'max_upload_mb']],
  ['Support', ['support_email', 'support_phone']],
];

export default function SystemSettingsPage() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('platform_settings').select('*').order('key');
    if (error) setMessage({ severity: 'error', text: error.message });
    setRows(data || []);
    setDraft(Object.fromEntries((data || []).map((r) => [r.key, r.value])));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setSavingKey(key);
    setMessage(null);
    // The RPC checks admin, applies the guard rails and writes the audit entry.
    // Doing it here in the browser would do none of those things.
    const { error } = await supabase.rpc('admin_set_platform_setting', {
      p_key: key, p_value: draft[key],
    });
    if (error) {
      setMessage({ severity: 'error', text: error.message });
    } else {
      setMessage({ severity: 'success', text: `${key} saved. The change is in the audit log.` });
      await load();
    }
    setSavingKey(null);
  };

  const control = (row) => {
    const value = draft[row.key];
    if (typeof row.value === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={!!value}
              onChange={(e) => setDraft({ ...draft, [row.key]: e.target.checked })}
            />
          }
          label={value ? 'On' : 'Off'}
        />
      );
    }
    const isNumber = typeof row.value === 'number';
    return (
      <TextField
        size="small"
        fullWidth
        value={value ?? ''}
        onChange={(e) =>
          setDraft({ ...draft, [row.key]: isNumber ? Number(e.target.value) : e.target.value })
        }
      />
    );
  };

  const grouped = GROUPS.map(([title, keys]) => [title, rows.filter((r) => keys.includes(r.key))]);
  const listed = new Set(GROUPS.flatMap(([, keys]) => keys));
  const other = rows.filter((r) => !listed.has(r.key));
  if (other.length) grouped.push(['Other', other]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>System Settings</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        These are read by the app and the payment functions as they run. Changing the commission
        changes what the next booking is charged. Every change is recorded with who made it and
        what it was before.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {loading ? <CircularProgress /> : (
        <Grid container spacing={3}>
          {grouped.map(([title, list]) => list.length > 0 && (
            <Grid item xs={12} md={6} key={title}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{title}</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {list.map((row) => (
                      <Box key={row.key}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {prettyStatus(row.key)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          {row.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {control(row)}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => save(row.key)}
                            disabled={savingKey === row.key || JSON.stringify(draft[row.key]) === JSON.stringify(row.value)}
                          >
                            Save
                          </Button>
                        </Box>
                        {row.updated_by && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            last changed by {row.updated_by}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Environment</Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2">
                  <strong>Supabase URL:</strong> {process.env.REACT_APP_SUPABASE_URL || 'Not configured'}
                </Typography>
                <Typography variant="body2">
                  <strong>Environment:</strong> {process.env.NODE_ENV || 'development'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
